import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { yellowFlagDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, StackedBarChart, fmtCount, type BarSegment } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import DrillLink from './DrillLink';
import { useDashboardList } from './useDashboardList';
import { YELLOW_FLAG_LIST } from './lists/yellowFlag';
import { DRILL_ROUTES, buildDrillUrl, monthRange, yearRange } from '../../utils/drilldown';

/**
 * Dashboard → Yellow Flag tab. Mirrors RedFlagTab's layout so the two read alike,
 * but the third module is deliberately DIFFERENT: yellow flags have no actions or
 * pushes tables, so there is no turnaround to measure. They are raised MANUALLY on
 * store visits, so the actionable equivalent is "who is raising them" — sourced from
 * yellow_flags.raised_by_code, which is already on the row.
 *
 * Everything here counts STANDING flags only (open/upheld, not cancelled) — the same
 * definition the former yellow-flag escalation used (removed 19 Oct 2026).
 */

interface ReasonMeta { key: string; label: string }
interface TrendDay { date: string; total: number; [reasonKey: string]: number | string }
interface TrendData { month: string; label: string; types: ReasonMeta[]; days: TrendDay[]; total: number }
interface LeaderRow { employeeCode: string; employeeName: string; storeName: string | null; flags: number; lastDate: string }
interface RaiserRow {
  raiserCode: string; raiserName: string; flags: number; employees: number;
  activeDays: number; perActiveDay: number; lastDate: string;
}
interface TodayRow {
  employeeCode: string; employeeName: string; storeName: string | null;
  raisedBy: string | null; flags: number; reasons: string;
}
/** A row of a pop-up's table: one employee's day in the bar drill, or one flag (with its
 *  id and date) in "Flags raised". */
type FlagRow = Omit<TodayRow, 'flags'> & { id?: string; date?: string };
/** "Flags raised": every flag the month's number counts, one row each, newest first. */
interface MonthFlags { month: string; label: string; total: number; rows: Array<FlagRow & { id: string; date: string }> }

// Yellow-flag reasons are per-project DB rows, not a fixed enum, so there is no
// stable key→hex map to hard-code the way RedFlagTab does. Colour by position in the
// derived reason list instead, anchored on yellow/amber so the tab reads as "yellow".
const PALETTE = ['#eab308', '#f59e0b', '#f97316', '#facc15', '#d97706', '#fbbf24', '#ca8a04', '#fb923c'];
const reasonHex = (idx: number): string => PALETTE[idx % PALETTE.length];

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '—';
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '—';
  const today = new Date();
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(dt)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** The table in a pop-up: the bar-click drill (one row per employee that day) and "Flags
 *  raised" (one row per flag, so `withDate` adds its Date). Its own component so its sort
 *  starts fresh each time a pop-up opens instead of carrying over from the last one. */
function FlagsTable({ rows, withDate = false }: { rows: FlagRow[]; withDate?: boolean }) {
  const sort = useTableSort<FlagRow>(
    rows,
    {
      date: (r) => r.date,
      employeeName: (r) => r.employeeName,
      storeName: (r) => r.storeName,
      reasons: (r) => r.reasons,
      raisedBy: (r) => r.raisedBy,
    },
    // The month's flags arrive newest first; the Date header says so. Flags on one day
    // keep the server's order, the most recently raised first.
    withDate ? { defaultKey: 'date', defaultDir: 'desc' } : undefined,
  );
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
        <tr>
          {withDate && <SortTh label="Date" sortKey="date" sort={sort} className="px-3 py-2 text-left font-medium" />}
          <SortTh label="Employee" sortKey="employeeName" sort={sort} className="px-3 py-2 text-left font-medium" />
          <SortTh label="Store" sortKey="storeName" sort={sort} className="px-3 py-2 text-left font-medium" />
          <SortTh label="Reason" sortKey="reasons" sort={sort} className="px-3 py-2 text-left font-medium" />
          <SortTh label="Raised by" sortKey="raisedBy" sort={sort} className="px-3 py-2 text-left font-medium" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {sort.sorted.map((r) => (
          // A person flagged twice is two rows in "Flags raised", so a flag is keyed by its id.
          <tr key={r.id ?? r.employeeCode}>
            {withDate && <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.date)}</td>}
            <td className="px-3 py-2">
              {r.employeeName}
              <div className="font-mono text-[10px] text-gray-400">{r.employeeCode}</div>
            </td>
            <td className="px-3 py-2 text-gray-600">{r.storeName || '—'}</td>
            <td className="px-3 py-2 text-gray-600">{r.reasons || '—'}</td>
            <td className="px-3 py-2 text-gray-600">{r.raisedBy || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── The month's number, beside the chart's title ──
// How many flags "Flags raised" lists, so it is a button that opens that list (the
// chevron says so, as on Red Flag's list cards). It keeps the amber it has always had
// here. The tab draws it only once the trend has answered, without an error, above zero:
// never from the 0 shown while loading or a number left over from a failed reload.
function MonthTotal({ count, onOpen }: { count: number; onOpen: () => void }): ReactElement {
  const value = fmtCount(count);
  const unit = count === 1 ? 'flag' : 'flags';
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Flags raised: ${value} ${unit}. Open the full list`}
      className="group inline-flex items-center gap-1 rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <span className="text-3xl font-bold tabular-nums tracking-tight text-amber-600 transition group-hover:text-amber-700">{value}</span>
      <ChevronRight className="h-3.5 w-3.5 text-gray-300 transition group-hover:text-primary-500" aria-hidden />
    </button>
  );
}

export default function YellowFlagTab({
  projectId, month, roleId, filters, reason,
}: {
  projectId: number | null;
  month: string;
  roleId?: string;
  filters?: DashRichFilterParams;
  reason?: string;
}) {
  const role = roleId && roleId.trim() !== '' ? roleId : undefined;
  const year = month.slice(0, 4);

  const [trend, setTrend] = useState<TrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [leaders, setLeaders] = useState<LeaderRow[]>([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbError, setLbError] = useState<string | null>(null);

  const [raisers, setRaisers] = useState<RaiserRow[]>([]);
  const [raisersLoading, setRaisersLoading] = useState(false);

  const [today, setToday] = useState<TodayRow[]>([]);
  const [todayDate, setTodayDate] = useState<string>('');
  const [todayLoading, setTodayLoading] = useState(false);

  // Day drill-down (click a bar).
  const [dayRows, setDayRows] = useState<TodayRow[] | null>(null);
  const [dayDate, setDayDate] = useState<string>('');
  const [dayLoading, setDayLoading] = useState(false);

  // "Flags raised" opens by address (?list=raised): the month's number and the home
  // screen's Yellow Flag card both write it, and what is open is read from it on every
  // render. Held in state it would be shut by the reload that follows the default Role
  // filter.
  const { list, openList, closeList } = useDashboardList();
  const raisedOpen = list === YELLOW_FLAG_LIST.raised;
  const [monthFlags, setMonthFlags] = useState<MonthFlags | null>(null);
  const [monthFlagsError, setMonthFlagsError] = useState<string | null>(null);
  // Bumped by the window's "Try again". A failure leaves ?list=raised in the address,
  // so opening the list again changes nothing the effect watches; this does.
  const [monthFlagsTry, setMonthFlagsTry] = useState(0);
  const monthFlagsReq = useRef(0);

  const common = { projectId: projectId ?? undefined, roleId: role, reason: reason || undefined, ...filters };

  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    setTrendLoading(true); setTrendError(null);
    yellowFlagDashboardAPI.getTrend({ ...common, month })
      .then((r) => { if (!cancelled) setTrend((r.data?.data as TrendData) || null); })
      .catch((e: any) => { if (!cancelled) setTrendError(e?.response?.data?.message || 'Failed to load yellow-flag trend'); })
      .finally(() => { if (!cancelled) setTrendLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, month, role, reason, JSON.stringify(filters)]);

  // "Flags raised" is asked only while it is open, with the trend's own `common` and
  // month, so it lists the flags the number beside the chart counts. Every change drops
  // the answer first (and a late reply to an earlier question is ignored), so rows asked
  // before the default Role filter landed are never shown under it. A failure, or an
  // answer without rows, is kept as an error for the window to say.
  useEffect(() => {
    const reqId = ++monthFlagsReq.current;
    setMonthFlags(null); setMonthFlagsError(null);
    if (projectId == null) return;
    if (!raisedOpen) return;
    const failed = 'Failed to load the month’s yellow flags';
    yellowFlagDashboardAPI.getMonthFlags({ ...common, month })
      .then((r) => {
        if (reqId !== monthFlagsReq.current) return;
        const data = r.data?.data as MonthFlags | undefined;
        if (data && Array.isArray(data.rows)) setMonthFlags(data);
        else setMonthFlagsError(failed);
      })
      .catch((e: any) => { if (reqId === monthFlagsReq.current) setMonthFlagsError(e?.response?.data?.message || failed); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, month, role, reason, JSON.stringify(filters), raisedOpen, monthFlagsTry]);

  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    setLbLoading(true); setLbError(null);
    yellowFlagDashboardAPI.getLeaderboard({ ...common, year })
      .then((r) => { if (!cancelled) setLeaders(((r.data?.data?.employees) as LeaderRow[]) || []); })
      .catch((e: any) => { if (!cancelled) setLbError(e?.response?.data?.message || 'Failed to load leaderboard'); })
      .finally(() => { if (!cancelled) setLbLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, year, role, reason, JSON.stringify(filters)]);

  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    setRaisersLoading(true);
    yellowFlagDashboardAPI.getRaisers({ ...common, month })
      .then((r) => { if (!cancelled) setRaisers(((r.data?.data?.raisers) as RaiserRow[]) || []); })
      .catch(() => { if (!cancelled) setRaisers([]); })
      .finally(() => { if (!cancelled) setRaisersLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, month, role, reason, JSON.stringify(filters)]);

  useEffect(() => {
    if (projectId == null) return;
    let cancelled = false;
    setTodayLoading(true);
    yellowFlagDashboardAPI.getToday({ ...common })
      .then((r) => {
        if (cancelled) return;
        setToday(((r.data?.data?.rows) as TodayRow[]) || []);
        setTodayDate(r.data?.data?.today || '');
      })
      .catch(() => { if (!cancelled) setToday([]); })
      .finally(() => { if (!cancelled) setTodayLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, role, reason, JSON.stringify(filters)]);

  const openDay = (date: string) => {
    if (projectId == null) return;
    setDayDate(date); setDayRows(null); setDayLoading(true);
    yellowFlagDashboardAPI.getTrendDay({ ...common, date })
      .then((r) => setDayRows(((r.data?.data?.rows) as TodayRow[]) || []))
      .catch(() => setDayRows([]))
      .finally(() => setDayLoading(false));
  };

  // One segment per reason present in the window. Reasons are per-project DB rows,
  // so both the segment list and its colours are derived per response.
  const segments: BarSegment[] = useMemo(
    () => (trend?.types ?? []).map((t, i) => ({ key: t.key, label: t.label, hex: reasonHex(i) })),
    [trend],
  );
  const visibleKeys = useMemo(() => new Set(segments.map((s) => s.key)), [segments]);
  const chartDays = useMemo(
    () => (trend?.days ?? []).map((d) => ({ ...d, label: Number(d.date.slice(8, 10)) })),
    [trend],
  );

  const leaderSort = useTableSort<LeaderRow>(
    leaders,
    {
      employeeName: (r) => r.employeeName,
      storeName: (r) => r.storeName ?? '',
      flags: (r) => r.flags,
      lastDate: (r) => r.lastDate ?? '',
    },
    { defaultKey: 'flags', defaultDir: 'desc' },
  );
  const raiserSort = useTableSort<RaiserRow>(
    raisers,
    {
      raiserName: (r) => r.raiserName,
      flags: (r) => r.flags,
      employees: (r) => r.employees,
      activeDays: (r) => r.activeDays,
      perActiveDay: (r) => r.perActiveDay,
      lastDate: (r) => r.lastDate ?? '',
    },
    { defaultKey: 'flags', defaultDir: 'desc' },
  );
  // The server already orders these rows flags DESC, then name — shown on the header,
  // as the leaderboard and raiser tables do.
  const todaySort = useTableSort<TodayRow>(
    today,
    {
      employeeName: (r) => r.employeeName,
      storeName: (r) => r.storeName,
      reasons: (r) => r.reasons,
      raisedBy: (r) => r.raisedBy,
      flags: (r) => r.flags,
    },
    { defaultKey: 'flags', defaultDir: 'desc' },
  );
  const sortedLeaders = leaderSort.sorted;
  const sortedRaisers = raiserSort.sorted;

  const mRange = monthRange(month);
  const yRange = yearRange(year);

  const downloadLeaders = () => {
    downloadCsv(
      `Yellow_Flags_Employees_${year}.csv`,
      buildCsv(
        ['Employee Code', 'Employee', 'Store', 'Yellow Flags', 'Last Flag'],
        sortedLeaders.map((r) => [r.employeeCode, r.employeeName, r.storeName || '', String(r.flags), r.lastDate || '']),
      ),
    );
  };
  const downloadRaisers = () => {
    downloadCsv(
      `Yellow_Flags_Raised_By_${month}.csv`,
      buildCsv(
        ['Raiser Code', 'Raised By', 'Flags', 'Employees', 'Active Days', 'Flags / Active Day', 'Last Raised'],
        sortedRaisers.map((r) => [
          r.raiserCode, r.raiserName, String(r.flags), String(r.employees),
          String(r.activeDays), String(r.perActiveDay), r.lastDate || '',
        ]),
      ),
    );
  };

  if (projectId == null) return <div className="p-6 text-sm text-gray-500">Select a project to view yellow flags.</div>;

  // The month's number opens "Flags raised" only once the trend has answered without an
  // error and found flags; otherwise it stays plain text, as it always was.
  const openableTotal = !trendLoading && !trendError && trend && trend.total > 0 ? trend.total : null;
  const flagsListed = monthFlags?.rows.length ?? 0;
  // The window's month while its answer is on the way: the trend's label, but only when
  // it answered this month (it keeps its last answer while it reloads after a new pick).
  const raisedLabel = monthFlags?.label || (trend?.month === month ? trend.label : month);

  return (
    <div className="space-y-6">
      {/* ── Trend ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Yellow flags raised — {trend?.label || month}</h3>
            <p className="text-xs text-gray-500">
              Standing flags only (open or upheld, not cancelled) — the same ones that count toward the escalation
              threshold. Click a bar for that day&apos;s employees.
            </p>
          </div>
          <div className="text-right">
            {openableTotal !== null ? (
              <MonthTotal count={openableTotal} onOpen={() => openList(YELLOW_FLAG_LIST.raised)} />
            ) : (
              <div className="text-3xl font-bold tabular-nums tracking-tight text-amber-600">{fmtCount(trend?.total ?? 0)}</div>
            )}
            <div className="text-xs text-gray-500">this month</div>
          </div>
        </div>
        {trendLoading ? (
          <div className="py-10 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : trendError ? (
          <div className="py-10 text-center text-sm text-red-600">{trendError}</div>
        ) : !trend || trend.total === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">No yellow flags raised in this period.</div>
        ) : (
          <>
            <StackedBarChart
              days={chartDays}
              segments={segments}
              visibleKeys={visibleKeys}
              getCount={(d, key) => Number((d as Record<string, unknown>)[key] ?? 0)}
              getDayTotal={(d) => Number(d.total ?? 0)}
              onSegmentClick={(d) => openDay(d.date)}
              tooltipLines={(_d, seg, count, dayTotal) => [
                `${count} ${count === 1 ? 'flag' : 'flags'} — ${seg.label}`,
                `${dayTotal} that day`,
              ]}
            />
            <div className="mt-3 flex flex-wrap gap-3">
              {segments.map((seg) => (
                <span key={seg.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: seg.hex }} />
                  {seg.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Today ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-1 text-lg font-semibold text-gray-900">Raised today{todayDate ? ` — ${fmtDate(todayDate)}` : ''}</h3>
        <p className="mb-3 text-xs text-gray-500">Employees who picked up a yellow flag today.</p>
        {todayLoading ? (
          <div className="py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : today.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">Nothing raised today.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <SortTh label="Employee" sortKey="employeeName" sort={todaySort} className="px-3 py-2 text-left font-medium" />
                  <SortTh label="Store" sortKey="storeName" sort={todaySort} className="px-3 py-2 text-left font-medium" />
                  <SortTh label="Reason" sortKey="reasons" sort={todaySort} className="px-3 py-2 text-left font-medium" />
                  <SortTh label="Raised by" sortKey="raisedBy" sort={todaySort} className="px-3 py-2 text-left font-medium" />
                  <SortTh label="Flags" sortKey="flags" sort={todaySort} align="center" className="px-3 py-2 text-center font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {todaySort.sorted.map((r) => (
                  <tr key={r.employeeCode} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <DrillLink
                        href={buildDrillUrl(DRILL_ROUTES.yellowFlags, {
                          employeeCode: r.employeeCode,
                          startDate: todayDate,
                          endDate: todayDate,
                        })}
                      >
                        {r.employeeName}
                      </DrillLink>
                      <div className="font-mono text-[10px] text-gray-400">{r.employeeCode}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.storeName || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{r.reasons || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{r.raisedBy || '—'}</td>
                    <td className="px-3 py-2 text-center font-semibold text-amber-600">{r.flags}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Leaderboard ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Most yellow-flagged employees — {year}</h3>
            <p className="text-xs text-gray-500">
              Calendar-year totals, which is the window the escalation rule uses — so this count IS an employee&apos;s
              progress toward an escalated red flag.
            </p>
          </div>
          {sortedLeaders.length > 0 && (
            <button
              type="button"
              onClick={downloadLeaders}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          )}
        </div>
        {lbLoading ? (
          <div className="py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : lbError ? (
          <div className="py-6 text-center text-sm text-red-600">{lbError}</div>
        ) : sortedLeaders.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">No yellow flags this year.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <SortTh label="Employee" sortKey="employeeName" sort={leaderSort} className="px-3 py-2 text-left" />
                  <SortTh label="Store" sortKey="storeName" sort={leaderSort} className="px-3 py-2 text-left" />
                  <SortTh label="Yellow Flags" sortKey="flags" sort={leaderSort} align="center" className="px-3 py-2 text-center" />
                  <SortTh label="Last Flag" sortKey="lastDate" sort={leaderSort} className="px-3 py-2 text-left" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedLeaders.map((r) => (
                  <tr key={r.employeeCode} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <DrillLink
                        href={buildDrillUrl(DRILL_ROUTES.yellowFlags, {
                          employeeCode: r.employeeCode,
                          startDate: yRange.startDate,
                          endDate: yRange.endDate,
                        })}
                      >
                        {r.employeeName}
                      </DrillLink>
                      <div className="font-mono text-[10px] text-gray-400">{r.employeeCode}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.storeName || '—'}</td>
                    <td className="px-3 py-2 text-center font-semibold text-amber-600">{r.flags}</td>
                    <td className="px-3 py-2 text-gray-600">{fmtDate(r.lastDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Who raised them (the yellow analogue of Red Flag's Action & TAT) ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Who raised them — {trend?.label || month}</h3>
            <p className="text-xs text-gray-500">
              Yellow flags are raised manually on store visits, so this shows who is actually using them.
              &ldquo;Per active day&rdquo; is flags divided by the days that person raised on, not by calendar days.
            </p>
          </div>
          {sortedRaisers.length > 0 && (
            <button
              type="button"
              onClick={downloadRaisers}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          )}
        </div>
        {raisersLoading ? (
          <div className="py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
        ) : sortedRaisers.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">Nobody raised a yellow flag this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <SortTh label="Raised by" sortKey="raiserName" sort={raiserSort} className="px-3 py-2 text-left" />
                  <SortTh label="Flags" sortKey="flags" sort={raiserSort} align="center" className="px-3 py-2 text-center" />
                  <SortTh label="Employees" sortKey="employees" sort={raiserSort} align="center" className="px-3 py-2 text-center" />
                  <SortTh label="Active Days" sortKey="activeDays" sort={raiserSort} align="center" className="px-3 py-2 text-center" />
                  <SortTh label="Per Active Day" sortKey="perActiveDay" sort={raiserSort} align="center" className="px-3 py-2 text-center" />
                  <SortTh label="Last Raised" sortKey="lastDate" sort={raiserSort} className="px-3 py-2 text-left" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRaisers.map((r) => (
                  <tr key={r.raiserCode} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <DrillLink
                        href={buildDrillUrl(DRILL_ROUTES.yellowFlags, {
                          managerCode: r.raiserCode,
                          startDate: mRange.startDate,
                          endDate: mRange.endDate,
                        })}
                      >
                        {r.raiserName}
                      </DrillLink>
                      <div className="font-mono text-[10px] text-gray-400">{r.raiserCode}</div>
                    </td>
                    <td className="px-3 py-2 text-center font-semibold text-amber-600">{r.flags}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{r.employees}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{r.activeDays}</td>
                    <td className="px-3 py-2 text-center text-gray-600">{r.perActiveDay}</td>
                    <td className="px-3 py-2 text-gray-600">{fmtDate(r.lastDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Day drill-down ── */}
      {dayDate && (
        <Modal title={`Yellow flags — ${fmtDate(dayDate)}`} onClose={() => { setDayDate(''); setDayRows(null); }}>
          {dayLoading ? (
            <div className="py-8 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
          ) : !dayRows || dayRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">No yellow flags on this day.</div>
          ) : (
            <FlagsTable rows={dayRows} />
          )}
        </Modal>
      )}

      {/* ── "Flags raised" ── open while the address names it; closing takes the name out
          of the address. Unlike the lists on other dashboards, its rows are asked for
          only after the click, so the window opens at once and says it is loading, as the
          bar drill does: a click that changed nothing would read as a broken button. The
          count is the rows it shows, never the trend's total: the two are separate
          requests. */}
      {raisedOpen && (
        <Modal title={`Flags raised — ${raisedLabel}`} onClose={closeList}>
          {monthFlagsError ? (
            <div role="alert" className="py-8 text-center text-sm">
              <p className="text-red-600">{monthFlagsError}</p>
              <button
                type="button"
                onClick={() => setMonthFlagsTry((n) => n + 1)}
                className="mt-2 font-medium text-primary-600 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : !monthFlags ? (
            <div className="py-8 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
          ) : flagsListed === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">No yellow flags raised in this period.</div>
          ) : (
            <>
              <div className="mb-2 text-xs text-gray-500">
                {fmtCount(flagsListed)} {flagsListed === 1 ? 'flag' : 'flags'}, newest first
              </div>
              <FlagsTable rows={monthFlags.rows} withDate />
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
